import { GetStaticPaths, GetStaticProps } from 'next'
import { FC } from 'react'
import { NextSeo } from 'next-seo'

import { strapi } from 'api'
import { REVALIDATION_ERROR_TIMEOUT, REVALIDATION_TIMEOUT } from 'const/config'
import { BackendError } from 'api/strapi/BackendError'
import { TeamPerson } from 'api/strapi/teams/types'
import { Heading } from 'components/heading'
import { Section } from 'components/section'
import { LinkButton } from 'components/links'
import { routes } from 'utils/routes'
import { TeamMemberAvatar } from 'components/team-members/team-member-avatar'
import { Markdown } from 'components/markdown'
import { Team } from 'components/team-members'
import { cn } from 'utils'
import { LinkedinLink } from 'components/linkedin-link'
import { normalizePersonPosition } from 'components/team-members/utils'

import classes from './team-member.module.css'
import { getAdaptiveImageSize } from '../../../utils/media'

interface Props {
  currentPerson: TeamPerson
  otherTeamPeople: TeamPerson[]
}

const avatarSize = getAdaptiveImageSize({
  default: '400px',
  tablet: '250px',
  mobile: '100vw',
})

const IndexPage: FC<Props> = ({ otherTeamPeople, currentPerson }) => {
  return (
    <>
      <NextSeo title={currentPerson.full_name} />
      <Section>
        <header className={classes.header}>
          <Heading theme="h1" className={classes.fullName}>
            {currentPerson.full_name}
          </Heading>

          <ul className={classes.positionsList}>
            {normalizePersonPosition(currentPerson.position).map((name, index) => (
              <li
                key={name}
                className={cn(classes.position, index === 0 && classes.primaryPosition)}
              >
                {name}
              </li>
            ))}
            {currentPerson.linkedin && (
              <li className={classes.linkedin}>
                <LinkedinLink
                  name={currentPerson.full_name}
                  href={currentPerson.linkedin}
                  className={classes.linkedinIcon}
                />
              </li>
            )}
          </ul>

          <LinkButton theme="goBack" href={routes.meetTheTeam} className={classes.meetTheTeamLink}>
            Meet The Team
          </LinkButton>
        </header>

        <div className={classes.content}>
          <div className={classes.avatarContainer}>
            <TeamMemberAvatar
              person={currentPerson}
              fill
              className={classes.avatar}
              sizes={avatarSize}
            />
          </div>

          <Markdown className={classes.quote}>{currentPerson.quote}</Markdown>

          <Markdown className={classes.biography}>{currentPerson.biography}</Markdown>
        </div>
      </Section>

      <Team
        classNameHeader={classes.otherTeamMembersHeader}
        classNameList={classes.otherTeamMembersList}
        people={otherTeamPeople}
        title="Meet the rest of the team"
        content={
          'While our backgrounds and skills are diverse, we share a common vision: to deliver unique, data-driven solutions that make a difference in the lives of our customers and investors, and in society at large. This who we are.\n'
        }
      />
    </>
  )
}

export default IndexPage

export const getStaticProps: GetStaticProps<Props> = async (context) => {
  const { id } = context.params ?? {}

  if (!id || Array.isArray(id)) {
    // eslint-disable-next-line no-console
    console.error(`Team Person Info by ${id} not found`)
    return {
      notFound: true,
      revalidate: REVALIDATION_ERROR_TIMEOUT,
    }
  }

  try {
    const [teams] = await Promise.all([strapi.teams.find()])

    let currentPerson: TeamPerson | null = null
    const otherTeamPeople = teams.items.filter((person) => {
      const isCurrentPerson = person.id === Number(id)

      if (isCurrentPerson) {
        currentPerson = person
      }

      return !isCurrentPerson
    })

    if (!currentPerson) {
      return {
        notFound: true,
        revalidate: REVALIDATION_ERROR_TIMEOUT,
      }
    }

    return {
      revalidate: REVALIDATION_TIMEOUT,
      props: {
        currentPerson,
        otherTeamPeople,
      },
    }
  } catch (error) {
    if ((error as BackendError).notFound) {
      return {
        notFound: true,
        revalidate: REVALIDATION_ERROR_TIMEOUT,
      }
    }
    throw error
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const ids = await strapi.teams.listIds()
  return {
    fallback: 'blocking',
    paths: ids.map((id) => ({
      params: {
        id: String(id),
      },
    })),
  }
}
